import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { setAuthHandlers } from '../api/client';

type Prompt = (reason: string) => Promise<string | null>;
const Ctx = createContext<Prompt>(async () => null);

/** Asks the owner for a fresh TOTP code; wired into the API client for 403 totp_required. */
export function TotpPromptProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const resolver = useRef<((v: string | null) => void) | null>(null);

  const prompt = useCallback<Prompt>((r) => {
    resolver.current?.(null);
    setReason(r);
    setCode('');
    setOpen(true);
    return new Promise((res) => {
      resolver.current = res;
    });
  }, []);

  useEffect(() => {
    setAuthHandlers({ promptTotp: prompt });
  }, [prompt]);

  const close = (v: string | null) => {
    setOpen(false);
    resolver.current?.(v);
    resolver.current = null;
  };
  const valid = /^\d{6}$/.test(code);

  return (
    <Ctx.Provider value={prompt}>
      {children}
      <Dialog open={open} onClose={() => close(null)} maxWidth="xs" fullWidth>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) close(code);
          }}
        >
          <DialogTitle>Confirm with 2FA</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>{reason || 'Enter a fresh code from your authenticator app.'}</DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', 'aria-label': '6-digit code', style: { letterSpacing: '0.3em', fontSize: '1.3rem' } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => close(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!valid}>
              Confirm
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Ctx.Provider>
  );
}

export const useTotpPrompt = () => useContext(Ctx);
